/* ════════════════════════════════════════════════════════════════════
   src/api/queries.js
   All React Query hooks live here. Section components only ever import
   from this file — they never call fetch() directly.

   Naming convention:
     useFooList     → paginated list
     useFooDetail   → single record
     useFooSummary  → stats / aggregates
     useFooFilters  → dropdown options
   ════════════════════════════════════════════════════════════════════ */

import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  fetchAdminPatientDetail,
  fetchAuditPage,
  fetchAuditExport,
  fetchAuditSummary,
  fetchAuditFilterOptions,
  fetchClinicsPage,
  fetchClinicsSummary,
  fetchClinicsFilterOptions,
  fetchDoctorsPage,
  fetchDoctorsSummary,
  fetchDoctorsFilterOptions,
  fetchStaffPage,
  fetchStaffSummary,
  fetchStaffFilterOptions,
  fetchOnboardingSchema,
  fetchOnboardingData,
  createOnboarding,
  saveOnboardingSection,
  activateOnboarding,
  fetchReports,
  fetchCompliance,
  fetchMasterForms,
  saveMasterFormsCategory,
  fetchMasterFormVersions,
  restoreMasterFormVersion,
  fetchProcedures,
  saveProcedures,
  fetchLabReference,
  saveLabReferenceCategory,
  fetchDrugsSummary,
  fetchDrugsFilterOptions,
  fetchDrugsPage,
  fetchDataCorrection,
  fetchPatientsPage,              // ← NEW
  fetchPatientsSummary,           // ← NEW
  fetchPatientsFilterOptions,     // ← NEW
  decideDataCorrection,
  fetchNotificationsPage,           // ← NEW
  fetchNotificationsSummary,        // ← NEW
  markNotificationRead,             // ← NEW
  markAllNotificationsRead,         // ← NEW
  fetchSettings,                  // ← NEW
  saveSettingsSection,            // ← NEW
  requestOtp, verifyOtp,                       // ← NEW
  signupRequest, signupVerify,                 // ← Practo-style self sign-up
  logout as apiLogout,                         // ← NEW
  getCurrentUser, isAuthenticated,             // ← NEW
  fetchMe,                                      // ← B144 dual-role refresh
  fetchDoctorDashboard,
  fetchDoctorEducation,
  fetchDoctorPromotion,
  fetchDoctorHelp,
  fetchPromotions,
  savePromotion,
  togglePromotion,
  deletePromotion,
  fetchClinicDrugs,
  saveClinicDrug,
  deleteClinicDrug,
  fetchClinicProcedures,
  saveClinicProcedure,
  deleteClinicProcedure,
  setClinicProcedurePrice,
  clearClinicProcedurePrice,
  fetchEffectiveDrugs,
  fetchEffectiveProcedures,
  fetchClinicMasterFormLists,
  fetchClinicMasterFormEntries,
  saveClinicMasterFormEntry,
  deleteClinicMasterFormEntry,
  fetchPkPlans,
  savePkPlan,
  deletePkPlan,
  fetchDoctorPatientsPage,          // ← NEW (Doctor app)
  fetchDoctorPatientsSummary,       // ← NEW (Doctor app)
  fetchDoctorPatientsFilterOptions, // ← NEW (Doctor app)
  fetchDoctorPatientDetail,
  fetchDoctorCalendar,
  saveDoctorSchedule,
  fetchDoctorSchedule,
  updateAppointmentStatus,
  rescheduleAppointment,
  reserveEmergencySlot,
  updateAppointment,
  openConsultation,           // ← NEW (Consultation)
  fetchConsultation,          // ← NEW (Consultation)
  fetchPreviousConsultation,
  fetchPatientDrugUsage,
  fetchPatientRecentComplaints,
  fetchConsultationTemplates,
  createConsultationTemplate,
  deleteConsultationTemplate,
  saveConsultationSection,    // ← NEW (Consultation)
  completeConsultation,       // ← NEW (Consultation)
  reopenConsultation,
  addConsultationAddendum,
  saveBodyMaps,
  saListConsultations,          // ← NEW (D16 SA)
  saReopenConsultation,         // ← NEW (D16 SA)
  fetchDoctorReports,           // ← NEW (Doctor reports)
  fetchStaffToday,              // ← NEW (Staff · Phase 7)
  fetchStaffBookingContext,     // ← NEW (Booking)
  fetchStaffSlots,              // ← NEW (Booking)
  fetchPatientSlots,           // patient-scoped slot lookup
  createStaffAppointment,       // ← NEW (Booking)
  convertEmergencyHold,         // ← NEW (Booking)
  fetchStaffFollowups,          // ← NEW (Batch 33)
  fetchBilling,                 // ← NEW (Billing)
  createInvoice,                // ← NEW (Billing)
  recordPayment,                // ← NEW (Billing)
  issueRefund,                  // ← NEW (Billing)
  fetchCoupons,                 // ← NEW (B107 Coupons)
  validateCoupon,               // ← NEW (B107 Coupons)
  saveCoupon,                   // ← NEW (B107 Coupons)
  toggleCoupon,                 // ← NEW (B107 Coupons)
  deleteCoupon,                 // ← NEW (B107 Coupons)
  fetchStaffDay,                // ← NEW (Batch 36)
  submitMergeRequest,           // ← NEW (Merge)
  fetchMyMergeRequests,         // ← NEW (Merge)
  fetchComplaints,              // ← NEW (Complaints)
  createComplaint,              // ← NEW (Complaints)
  updateComplaint,              // ← NEW (Complaints)
  fetchMasseurDay,              // ← NEW (Masseur)
  updatePkSession,              // ← NEW (Masseur)
  fetchPkTemplates,             // ← NEW (PK sheet)
  createPkCourse,               // ← NEW (PK sheet)
  fetchPkCourses,               // ← NEW (PK sheet)
  fetchPkConsent,               // ← NEW (PK consent)
  sendPkConsentOtp,             // ← NEW
  verifyPkConsent,              // ← NEW
  fetchPkCourse,                // ← NEW (PK sheet)
  updatePkCourse,               // ← NEW (PK sheet)
  fetchDoctorAccounts,          // ← NEW (Accounts)
  adjustInventory,              // ← NEW (Accounts)
  fetchDoctorSettings,          // ← NEW (Settings)
  fetchDoctorOnboardingStatus,  // ← NEW (Onboarding docs)
  submitDoctorKyc,              // ← NEW (Onboarding docs)
  fetchClinicOnboardingStatus,  // ← NEW (Onboarding docs · clinic)
  submitClinicKyc,              // ← NEW (Onboarding docs · clinic)
  saveDoctorSettings,          // ← NEW (Settings)
  fetchPharmacyQueue,           // ← NEW (Pharmacy)
  dispenseOrder,               // ← NEW (Pharmacy)
  fetchPkConsumables,           // ← NEW (PK consumables · Assistant T3)
  addPkConsumable,             // ← NEW (PK consumables · Assistant T3)
  adjustPkConsumable,          // ← NEW (PK consumables · Assistant T3)
  fetchRmoQueue,                // ← NEW (RMO)
  submitRmoClearance,          // ← NEW (RMO)
  fetchAccountsExtras,          // ← NEW (B45)
  addExpenditure,               // ← NEW (B45)
  markExpenditurePaid,          // ← NEW (B45)
  createPurchaseOrder,          // ← NEW (B45)
  receivePurchaseOrder,         // ← NEW (B45)
  toggleCompliance,             // ← NEW (B45)
  fetchCosting,                 // ← NEW (B45)
  saveCosting,                  // ← NEW (B45)
  fetchMyClinicConfig,         // ← NEW (B45)
  fetchIncidents,               // ← NEW (Incidents)
  createIncident,              // ← NEW (Incidents)
  resolveIncident,             // ← NEW (Incidents)
  fetchRmoMonitor,             // ← NEW (B47)
  fetchMasseurHistory,         // ← NEW (B47)
  fetchPatientHome,            // ← NEW (Patient)
  fetchPatientAppointments,    // ← NEW (Patient)
  fetchPatientBookingContext,  // ← NEW (Patient)
  createPatientAppointment,    // ← NEW (Patient)
  cancelPatientAppointment,    // ← NEW (Patient)
  fetchPatientRecords,         // ← NEW (B49)
  fetchPatientRecord,          // ← NEW (B49)
  fetchPatientPkJourney,       // ← NEW (B49)
  fetchPatientLabReports,      // ← NEW (B137)
  fetchPatientImages,          // ← NEW (B137)
  fetchPatientUploads,         // ← NEW (B138)
  uploadPatientFile,           // ← NEW (B138)
  deletePatientUpload,         // ← NEW (B138)
  fetchClinicPatientLabs,      // ← NEW (B141)
  createClinicPatientLab,      // ← NEW (B141)
  fetchClinicDaySummary,       // ← NEW (B142)
  deleteClinicPatientLab,      // ← NEW (B141)
  fetchClinicPatientUploads,   // ← NEW (B141)
  uploadClinicPatientFile,     // ← NEW (B141)
  deleteClinicPatientUpload,   // ← NEW (B141)
  fetchPatientBills,           // ← NEW (B50)
  fetchPatientBill,            // ← NEW (B50)
  initiatePatientPayment,      // ← NEW (B50)
  verifyPatientPayment,        // ← NEW (Razorpay)
  fetchPatientProfile,         // ← NEW (B50)
  updateNotificationPrefs,
  requestProfileUpdate,        // ← NEW (B50)
  fetchPatientConsent,         // ← NEW (B51)
  signPatientConsent,          // ← NEW (B51)
  filePatientComplaint,        // ← NEW (B51)
  fetchMyComplaints,           // ← NEW (B51)
  fetchFeedbackTargets,        // ← NEW (B51)
  submitPatientFeedback,       // ← NEW (B51)
  quickAddPatient,             // ← NEW (B52)
  fetchMasseurCalendar,        // ← NEW (B52)
  fetchClinicPanel,            // ← NEW (B54)
  fetchClinicPatientsSearch,
  fetchMyClinics,              // ← NEW (B54)
  createClinicStaff,           // ← NEW (B54)
  fetchClinicBillingSettings,  // ← NEW (B56)
  saveClinicBillingSettings,   // ← NEW (B56)
  updateClinicDetails,         // ← NEW (B57)
  openShortConsultation,       // ← NEW (B58)
  fetchConsultationRefData,    // ← NEW (B58)
} from './client.js';

import {
  logPkDay,
  createPaymentOrder as _createPaymentOrder,
  verifyPayment as _verifyPayment,
  staffInvoiceOnlineOrder,        // ← NEW (online invoice pay)
  staffInvoiceOnlineVerify,       // ← NEW
  patientBillOnlineOrder,         // ← NEW
  patientBillOnlineVerify,        // ← NEW
  loadRazorpay,                   // ← NEW
  importDrugs,                    // ← NEW (bulk import)
  fetchPatient,                   // ← NEW
  setCommConsent,                 // ← NEW
} from './client.js';

import {
  fetchPublicClinics,
  fetchPublicSlots,
  fetchReturningPatient,
  publicBook,
} from './client.js';

import {
  fetchPublicOnboard,
  sendOnboardOtp,
  verifyOnboardOtp,
  submitOnboardDoc,
  fetchOnboardSchema,
  saveOnboardSection,
  fetchInvites,
  createInvite,
  resendInvite,
  revokeInvite,
  fetchKycSubjects,
  fetchKycSubject,
  verifyKycDoc,
  rejectKycDoc,
  approveKycSubject,
  fetchRegisterClinics,
  sendRegisterOtp,
  registerDoctor,
  registerClinic,
} from './client.js';

import {
  fetchCommOverview,
  fetchCommMessages,
  fetchCommThread,
  sendCommMessage,
  replyCommMessage,
  markCommRead,
  fetchCommTemplates,
  fetchCommDirectory,
  fetchCommCampaigns,
  createCommCampaign,
  fetchCommCertificates,
  issueCommCertificate,
} from './client.js';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

/* ──────────────────────────── Query keys ──────────────────────────── */

export const QK = {
  comm: {
    all:       ['comm'],
    overview:  ['comm', 'overview'],
    messages:  (f) => ['comm', 'messages', f],
    thread:    (id) => ['comm', 'thread', id],
    templates: ['comm', 'templates'],
    directory: ['comm', 'directory'],
    campaigns: ['comm', 'campaigns'],
    certs:     ['comm', 'certs'],
  },
  audit: {
    all:     ['audit'],
    list:    (filters) => ['audit', 'list', filters],
    summary: ['audit', 'summary'],
    filters: ['audit', 'filters'],
  },
  clinics: {
    all:     ['clinics'],
    list:    (filters) => ['clinics', 'list', filters],
    summary: ['clinics', 'summary'],
    filters: ['clinics', 'filters'],
  },
  doctors: {
    all:     ['doctors'],
    list:    (filters) => ['doctors', 'list', filters],
    summary: ['doctors', 'summary'],
    filters: ['doctors', 'filters'],
  },
  staff: {
    all:     ['staff'],
    list:    (filters) => ['staff', 'list', filters],
    summary: ['staff', 'summary'],
    filters: ['staff', 'filters'],
  },
  onboarding: {
    all:     ['onboarding'],
    schema:  (kind) => ['onboarding', 'schema', kind],
    data:    (kind, id) => ['onboarding', 'data', kind, id],
  },
  reports: {
    all:    ['reports'],
    bundle: (period) => ['reports', 'bundle', period || 'current'],
  },
  compliance: {
    all:  ['compliance'],
    year: (y) => ['compliance', 'year', y || 'current'],
  },
  masterForms: {
    all: ['master-forms'],
  },
  procedures: {
    all: ['procedures'],
  },
  labReference: {
    all: ['lab-reference'],
  },
  drugs: {
    all:           ['drugs'],
    summary:       ['drugs', 'summary'],
    filterOptions: ['drugs', 'filter-options'],
    page:          (filters) => ['drugs', 'page', filters],
  },
  dataCorrection: {
    all: ['data-correction'],
  },
  auth: {
    me: ['auth', 'me'],
  },
  patients: {
    all:     ['patients'],
    list:    (filters) => ['patients', 'list', filters],
    summary: ['patients', 'summary'],
    filters: ['patients', 'filters'],
  },
  notifications: {
    all:     ['notifications'],
    list:    (filters) => ['notifications', 'list', filters],
    summary: ['notifications', 'summary'],
  },
  settings: {
    all: ['settings'],
  },
  doctorDashboard: {
    all: ['doctor', 'dashboard'],
  },
  doctorPatients: {
    all:     ['doctor', 'patients'],
    list:    (filters) => ['doctor', 'patients', 'list', filters],
    summary: ['doctor', 'patients', 'summary'],
    filters: ['doctor', 'patients', 'filters'],
    detail:  (id) => ['doctor', 'patients', 'detail', id],
  },
  doctorCalendar: {
    all:   ['doctor', 'calendar'],
    range: (from, to) => ['doctor', 'calendar', 'range', from, to],
  },
  doctorSchedule: {
    all: ['doctor', 'schedule'],
  },
  consultation: {
    all:    ['doctor', 'consultation'],
    record: (id) => ['doctor', 'consultation', 'record', id],
  },
};

/* ──────────────────────────── Audit ──────────────────────────── */

/**
 * Paginated, filtered audit log.
 *
 * Returns a useInfiniteQuery result. Use `data.pages.flatMap(p => p.entries)`
 * to get the flat list, and `fetchNextPage()` to load more.
 *
 * `filters` is a plain object; changing any value invalidates the cache
 * and triggers a fresh fetch (with the very first page).
 */
export function useAuditList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.audit.list(filters),
    queryFn: ({ pageParam }) =>
      fetchAuditPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    // Audit log shouldn't auto-refresh — entries are immutable
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

/** On-demand full-trail fetch for Excel export (not a hook — call it when exporting). */
export { fetchAuditExport };

/** Recent audit rows for one clinic (Clinic Panel tab). A Clinic Admin is
 *  auto-scoped server-side; passing clinic here scopes a Super Admin drill-down. */
export function useClinicAudit(clinicId) {
  return useQuery({
    queryKey: ['clinicAudit', clinicId],
    queryFn: () => fetchAuditPage({ clinic: clinicId, limit: 50 }),
    staleTime: 30_000,
    enabled: !!clinicId,
  });
}

export function useAuditSummary() {
  return useQuery({
    queryKey: QK.audit.summary,
    queryFn: fetchAuditSummary,
    staleTime: 60_000,
  });
}

export function useAuditFilterOptions() {
  return useQuery({
    queryKey: QK.audit.filters,
    queryFn: fetchAuditFilterOptions,
    // Filter options change very rarely — cache for a long time
    staleTime: 10 * 60_000,
  });
}

/* ──────────────────────────── Clinics ──────────────────────────── */

/**
 * Paginated, filtered clinics list.
 *
 * Most networks only have a few dozen clinics so virtualization is overkill —
 * but the infinite-query shape stays consistent with audit/doctors/etc.
 */
export function useClinicsList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.clinics.list(filters),
    queryFn: ({ pageParam }) =>
      fetchClinicsPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    // Clinic records change rarely — 5 minutes is fine
    staleTime: 5 * 60_000,
  });
}

export function useClinicsSummary() {
  return useQuery({
    queryKey: QK.clinics.summary,
    queryFn: fetchClinicsSummary,
    staleTime: 5 * 60_000,
  });
}

export function useClinicsFilterOptions() {
  return useQuery({
    queryKey: QK.clinics.filters,
    queryFn: fetchClinicsFilterOptions,
    staleTime: 10 * 60_000,
  });
}
/* ──────────────────────────── Doctors ──────────────────────────── */

export function useDoctorsList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.doctors.list(filters),
    queryFn: ({ pageParam }) =>
      fetchDoctorsPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 2 * 60_000,
  });
}

export function useDoctorsSummary() {
  return useQuery({
    queryKey: QK.doctors.summary,
    queryFn: fetchDoctorsSummary,
    staleTime: 2 * 60_000,
  });
}

export function useDoctorsFilterOptions() {
  return useQuery({
    queryKey: QK.doctors.filters,
    queryFn: fetchDoctorsFilterOptions,
    staleTime: 10 * 60_000,
  });
}

/* ──────────────────────────── Staff ──────────────────────────── */

export function useStaffList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.staff.list(filters),
    queryFn: ({ pageParam }) =>
      fetchStaffPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 2 * 60_000,
  });
}

export function useStaffSummary() {
  return useQuery({
    queryKey: QK.staff.summary,
    queryFn: fetchStaffSummary,
    staleTime: 2 * 60_000,
  });
}

export function useStaffFilterOptions() {
  return useQuery({
    queryKey: QK.staff.filters,
    queryFn: fetchStaffFilterOptions,
    staleTime: 10 * 60_000,
  });
}

/* ──────────────────────────── Onboarding ──────────────────────────── */

/**
 * Fetch the schema for a kind of onboarding (doctor / staff / drug).
 * Cached aggressively — schemas rarely change.
 */
export function useOnboardingSchema(kind) {
  return useQuery({
    queryKey: QK.onboarding.schema(kind),
    queryFn: () => fetchOnboardingSchema(kind),
    enabled: !!kind,
    staleTime: 60 * 60_000, // 1 hour
  });
}

/**
 * Fetch saved draft + _meta for one record.
 * Pass `id = null` for the "new record" case — returns an empty shape.
 */
export function useOnboardingData(kind, id) {
  return useQuery({
    queryKey: QK.onboarding.data(kind, id),
    queryFn: () => fetchOnboardingData(kind, id),
    enabled: !!kind && !!id,
    staleTime: 30_000,
  });
}

/**
 * Mutation for Section 1 (the "create" section).
 * On success, invalidates the list query so the new record shows up,
 * and seeds the cache for the draft so the form can switch to edit mode
 * without a refetch.
 *
 * Usage:
 *   const create = useCreateOnboarding('doctor');
 *   const { id } = await create.mutateAsync({ sectionId: 'personal', values });
 */
export function useCreateOnboarding(kind, opts) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, values }) => createOnboarding(kind, sectionId, values, opts),
    onSuccess: (result) => {
      // Seed the draft cache so the form can read it immediately
      qc.setQueryData(QK.onboarding.data(kind, result.id), result);
      // Invalidate the list so the row appears (or gets a fresh status badge)
      qc.invalidateQueries({ queryKey: [`${kind}s`] });
      // Doctors/staff/patients created here now join their masters (B54) —
      // refresh the clinic panel and any scoped view of them.
      qc.invalidateQueries({ queryKey: ['clinic-panel'] });
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: ['doctor'] });
    },
  });
}

/**
 * Mutation for Sections 2..N — PATCH a single section's values.
 *
 * Usage:
 *   const save = useSaveOnboardingSection('doctor', regNo);
 *   await save.mutateAsync({ sectionId: 'education', values });
 */
export function useSaveOnboardingSection(kind, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, values }) => saveOnboardingSection(kind, id, sectionId, values),
    onSuccess: (result, vars) => {
      // Merge the saved section into the cached draft
      qc.setQueryData(QK.onboarding.data(kind, id), (prev) => ({
        ...(prev || {}),
        [vars.sectionId]: vars.values,
        _meta: result._meta,
      }));
    },
  });
}

export function useActivateOnboarding(kind, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => activateOnboarding(kind, id),
    onSuccess: () => {
      // Invalidate list queries so the row's status chip flips to Active
      qc.invalidateQueries({ queryKey: [`${kind}s`] });
      qc.invalidateQueries({ queryKey: ['clinic-panel'] });
      qc.invalidateQueries({ queryKey: QK.onboarding.data(kind, id) });
    },
  });
}

/* ──────────────────────────── Reports ──────────────────────────── */

/**
 * Cross-clinic pre-aggregated reports for a given period.
 * Cached for 10 minutes — reports are slow to compute on the backend
 * and don't need to be real-time.
 */
export function useReports(period) {
  return useQuery({
    queryKey: QK.reports.bundle(period),
    queryFn: () => fetchReports(period),
    staleTime: 10 * 60_000,
  });
}

/* ──────────────────────────── Compliance ──────────────────────────── */

/**
 * Compliance grid + upcoming deadlines for a fiscal year.
 * Cached for 5 minutes — compliance status changes only when someone
 * files a return, which is infrequent.
 */
export function useCompliance(year) {
  return useQuery({
    queryKey: QK.compliance.year(year),
    queryFn: () => fetchCompliance(year),
    staleTime: 5 * 60_000,
  });
}
/* ──────────────────────────── Master forms ──────────────────────────── */

export function useMasterForms() {
  return useQuery({
    queryKey: QK.masterForms.all,
    queryFn: fetchMasterForms,
    staleTime: 5 * 60_000,
  });
}

/**
 * Mutation for saving one category's full updated array.
 * Pattern: child component manages local edits, calls mutate on every change
 * (with built-in debounce on the caller side if needed).
 *
 * Usage:
 *   const save = useSaveMasterFormsCategory();
 *   save.mutate({ category: 'consultation', subKey: 'sections', items: [...] });
 */
export function useSaveMasterFormsCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, subKey, items }) =>
      saveMasterFormsCategory(category, subKey, items),
    onSuccess: (_result, vars) => {
      // Merge the saved array back into the cached bundle so other tabs
      // and re-renders see the latest values.
      qc.setQueryData(QK.masterForms.all, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [vars.category]: {
            ...prev[vars.category],
            [vars.subKey]: vars.items,
          },
        };
      });
    },
  });
}
/* ──────────────────────────── Procedures ──────────────────────────── */

export function useProcedures() {
  return useQuery({
    queryKey: QK.procedures.all,
    queryFn: fetchProcedures,
    staleTime: 5 * 60_000,
  });
}

export function useSaveProcedures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subKey, payload }) => saveProcedures(subKey, payload),
    onSuccess: (_result, vars) => {
      qc.setQueryData(QK.procedures.all, (prev) => {
        if (!prev) return prev;
        return { ...prev, [vars.subKey]: vars.payload };
      });
    },
  });
}

/* ──────────────────────────── Lab reference ──────────────────────────── */

export function useLabReference() {
  return useQuery({
    queryKey: QK.labReference.all,
    queryFn: fetchLabReference,
    staleTime: 10 * 60_000,  // reference data — rarely changes
  });
}

export function useSaveLabReferenceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, payload }) => saveLabReferenceCategory(category, payload),
    onSuccess: (_result, vars) => {
      qc.setQueryData(QK.labReference.all, (prev) => {
        if (!prev) return prev;
        return { ...prev, [vars.category]: vars.payload };
      });
    },
  });
}
/* ──────────────────────────── Drugs ──────────────────────────── */

export function useDrugsSummary() {
  return useQuery({
    queryKey: QK.drugs.summary,
    queryFn: fetchDrugsSummary,
    staleTime: 5 * 60_000,
  });
}

export function useDrugsFilterOptions() {
  return useQuery({
    queryKey: QK.drugs.filterOptions,
    queryFn: fetchDrugsFilterOptions,
    staleTime: 10 * 60_000,
  });
}

export function useDrugsPage(filters) {
  return useInfiniteQuery({
    queryKey: QK.drugs.page(filters),
    queryFn: ({ pageParam }) => fetchDrugsPage({ ...filters, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 2 * 60_000,
  });
}

/* ──────────────────────────── Data correction ──────────────────────────── */

export function useDataCorrection() {
  return useQuery({
    queryKey: QK.dataCorrection.all,
    queryFn: fetchDataCorrection,
    staleTime: 60_000,  // 1 minute — these are review queues, should feel fresh
  });
}

export function useDecideDataCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tab, id, decision }) => decideDataCorrection(tab, id, decision),
    onSuccess: (_result, vars) => {
      // Optimistically update the cached bundle
      qc.setQueryData(QK.dataCorrection.all, (prev) => {
        if (!prev) return prev;
        const arr = prev[vars.tab];
        if (!Array.isArray(arr)) return prev;
        const newStatus = vars.decision.action === 'approve' ? 'approved' : 'rejected';
        return {
          ...prev,
          summary: {
            ...prev.summary,
            openRequests: Math.max(0, prev.summary.openRequests - 1),
            resolvedThisWeek: prev.summary.resolvedThisWeek + 1,
          },
          [vars.tab]: arr.map((r) =>
            r.id === vars.id
              ? {
                  ...r,
                  status: newStatus,
                  reviewedBy: 'Dr Devi Prasad Rao',
                  reviewedAt: new Date().toISOString(),
                  reviewNote: vars.decision.note || null,
                }
              : r
          ),
        };
      });
    },
  });
}


/* ──────────────────────────── Auth ──────────────────────────── */

export function useRequestOtp() {
  return useMutation({
    mutationFn: (phone) => requestOtp(phone),
  });
}

export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ challengeId, otp }) => verifyOtp(challengeId, otp),
    onSuccess: (result) => {
      qc.setQueryData(QK.auth.me, result.user);
    },
  });
}

/* Practo-style self sign-up — mirror the OTP hooks. */
export function useSignupRequest() {
  return useMutation({
    mutationFn: (phone) => signupRequest(phone),
  });
}

export function useSignupVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ challengeId, otp, name, email }) => signupVerify({ challengeId, otp, name, email }),
    onSuccess: (result) => {
      qc.setQueryData(QK.auth.me, result.user);
    },
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: QK.auth.me,
    // Render instantly from the cached login snapshot, then refresh from the
    // server so user.roles reflects any change (e.g. a doctor becoming their
    // clinic's admin) without a manual log out / in. This is what makes the
    // role switcher appear for a dual-role login.
    queryFn: fetchMe,
    initialData: () => getCurrentUser(),
    // Treat the cached snapshot as already stale so /me refetches on mount
    // (the user still sees the cached copy instantly — no flash).
    initialDataUpdatedAt: 0,
    staleTime: 30_000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return () => {
    apiLogout();
    qc.clear();
    window.location.href = '/login';
  };
}

export { isAuthenticated };

/* ──────────────────────────── Patients ──────────────────────────── */

export function usePatientsList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.patients.list(filters),
    queryFn: ({ pageParam }) =>
      fetchPatientsPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 2 * 60_000,
  });
}

export function usePatientsSummary() {
  return useQuery({
    queryKey: QK.patients.summary,
    queryFn: fetchPatientsSummary,
    staleTime: 2 * 60_000,
  });
}

export function usePatientsFilterOptions() {
  return useQuery({
    queryKey: QK.patients.filters,
    queryFn: fetchPatientsFilterOptions,
    staleTime: 10 * 60_000,
  });
}


/* ──────────────────────────── Notifications ──────────────────────────── */

export function useNotificationsList(filters = {}, options = {}) {
  return useInfiniteQuery({
    queryKey: QK.notifications.list(filters),
    queryFn: ({ pageParam }) =>
      fetchNotificationsPage({ ...filters, cursor: pageParam, limit: 25 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60_000,
    // Callers (e.g. the bell) can gate this so the list only loads when the
    // dropdown is actually opened — one fewer request on every page load.
    ...options,
  });
}

export function useNotificationsSummary() {
  return useQuery({
    queryKey: QK.notifications.summary,
    queryFn: fetchNotificationsSummary,
    // Refetch every 60 seconds so the bell stays current
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: (result) => {
      qc.setQueryData(QK.notifications.summary, result.summary);
      qc.invalidateQueries({ queryKey: QK.notifications.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: (result) => {
      qc.setQueryData(QK.notifications.summary, result.summary);
      qc.invalidateQueries({ queryKey: QK.notifications.all });
    },
  });
}

/* ──────────────────────────── Settings ──────────────────────────── */

export function useSettings() {
  return useQuery({
    queryKey: QK.settings.all,
    queryFn: fetchSettings,
    staleTime: 5 * 60_000,
  });
}

export function useSaveSettingsSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ section, values }) => saveSettingsSection(section, values),
    onSuccess: (result, vars) => {
      qc.setQueryData(QK.settings.all, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [vars.section]: { ...prev[vars.section], values: result.values },
        };
      });
    },
  });
}

/* ──────────────────────────── Doctor · Dashboard ──────────────────────────── */

export function useDoctorDashboard() {
  return useQuery({
    queryKey: QK.doctorDashboard.all,
    queryFn: fetchDoctorDashboard,
    staleTime: 60_000,
  });
}

/* ── Doctor dashboard modules (B111) ── */
export function useDoctorEducation() {
  return useQuery({ queryKey: ['doctor', 'education'], queryFn: fetchDoctorEducation, staleTime: 300_000 });
}
export function useDoctorPromotion() {
  return useQuery({ queryKey: ['doctor', 'promotion'], queryFn: fetchDoctorPromotion, staleTime: 60_000 });
}
export function useDoctorHelp() {
  return useQuery({ queryKey: ['doctor', 'help'], queryFn: fetchDoctorHelp, staleTime: 300_000 });
}

/* ── Promotions management (B113) ── */
export function usePromotions(clinicId) {
  return useQuery({
    queryKey: ['promotions', clinicId || 'network'],
    queryFn: () => fetchPromotions(clinicId),
    staleTime: 30_000,
  });
}
function usePromotionMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotions'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'promotion'] });
    },
  });
}
export function useSavePromotion()   { return usePromotionMutation(savePromotion); }
export function useTogglePromotion() { return usePromotionMutation(togglePromotion); }
export function useDeletePromotion() { return usePromotionMutation(deletePromotion); }

/* ── Clinic drug master (B115) ── */
export function useClinicDrugs(clinicId, filters = {}) {
  return useQuery({
    queryKey: ['clinicDrugs', clinicId, filters],
    queryFn: () => fetchClinicDrugs({ clinicId, ...filters }),
    staleTime: 30_000,
    enabled: !!clinicId,
  });
}
function useClinicDrugMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinicDrugs'] }),
  });
}
export function useSaveClinicDrug()   { return useClinicDrugMutation(saveClinicDrug); }
export function useDeleteClinicDrug() { return useClinicDrugMutation(({ id, clinicId }) => deleteClinicDrug(id, clinicId)); }

/* ── Clinic procedure master (B116) ── */
export function useClinicProcedures(clinicId, filters = {}) {
  return useQuery({
    queryKey: ['clinicProcedures', clinicId, filters],
    queryFn: () => fetchClinicProcedures({ clinicId, ...filters }),
    staleTime: 30_000,
    enabled: !!clinicId,
  });
}
function useClinicProcedureMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinicProcedures'] }),
  });
}
export function useSaveClinicProcedure()   { return useClinicProcedureMutation(saveClinicProcedure); }
export function useDeleteClinicProcedure() { return useClinicProcedureMutation(({ id, clinicId }) => deleteClinicProcedure(id, clinicId)); }
export function useSetClinicProcedurePrice()   { return useClinicProcedureMutation(setClinicProcedurePrice); }
export function useClearClinicProcedurePrice() { return useClinicProcedureMutation(({ id, clinicId }) => clearClinicProcedurePrice(id, clinicId)); }

/* ── Effective formularies (network + clinic) for prescribing & billing (B117) ── */
export function useEffectiveDrugs() {
  return useQuery({ queryKey: ['effectiveDrugs'], queryFn: fetchEffectiveDrugs, staleTime: 120_000 });
}
export function useEffectiveProcedures() {
  return useQuery({ queryKey: ['effectiveProcedures'], queryFn: fetchEffectiveProcedures, staleTime: 120_000 });
}

/* ── Clinic master-form overlay (B118) ── */
export function useClinicMasterFormLists(clinicId) {
  return useQuery({ queryKey: ['clinicMasterForms', clinicId], queryFn: () => fetchClinicMasterFormLists(clinicId), staleTime: 60_000, enabled: !!clinicId });
}
export function useClinicMasterFormEntries(path, clinicId) {
  return useQuery({ queryKey: ['clinicMasterFormEntries', clinicId, path], queryFn: () => fetchClinicMasterFormEntries(path, clinicId), staleTime: 30_000, enabled: !!clinicId && !!path });
}
function useClinicMasterFormMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinicMasterFormEntries'] });
      qc.invalidateQueries({ queryKey: ['clinicMasterForms'] });
    },
  });
}
export function useSaveClinicMasterFormEntry()   { return useClinicMasterFormMutation(saveClinicMasterFormEntry); }
export function useDeleteClinicMasterFormEntry() { return useClinicMasterFormMutation(({ id, path, clinicId }) => deleteClinicMasterFormEntry(id, path, clinicId)); }

/* ── Panchakarma plans (B125) ── */
export function usePkPlans(clinicId) {
  return useQuery({ queryKey: ['pkPlans', clinicId], queryFn: () => fetchPkPlans(clinicId), staleTime: 60_000, enabled: !!clinicId });
}
function usePkPlanMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pkPlans'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-templates'] });
    },
  });
}
export function useSavePkPlan()   { return usePkPlanMutation(savePkPlan); }
export function useDeletePkPlan() { return usePkPlanMutation(deletePkPlan); }

/* ──────────────────────────── Doctor · Patients (panel) ──────────────────────────── */

export function useDoctorPatientsList(filters = {}) {
  return useInfiniteQuery({
    queryKey: QK.doctorPatients.list(filters),
    queryFn: ({ pageParam }) =>
      fetchDoctorPatientsPage({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 2 * 60_000,
  });
}

export function useDoctorPatientsSummary() {
  return useQuery({
    queryKey: QK.doctorPatients.summary,
    queryFn: fetchDoctorPatientsSummary,
    staleTime: 2 * 60_000,
  });
}

export function useDoctorPatientsFilterOptions() {
  return useQuery({
    queryKey: QK.doctorPatients.filters,
    queryFn: fetchDoctorPatientsFilterOptions,
    staleTime: 10 * 60_000,
  });
}
export function useDoctorPatientDetail(id) {
  return useQuery({
    queryKey: QK.doctorPatients.detail(id),
    queryFn: () => fetchDoctorPatientDetail(id),
    enabled: !!id,
    staleTime: 60_000,
  });
}
/* ──────────────────────────── Doctor · Calendar ──────────────────────────── */

export function useDoctorCalendar(from, to) {
  return useQuery({
    queryKey: QK.doctorCalendar.range(from, to),
    queryFn: () => fetchDoctorCalendar({ from, to }),
    enabled: !!from && !!to,
    staleTime: 60_000,
  });
}
/* ──────────────────────────── Doctor · Schedule config ──────────────────────────── */

export function useDoctorSchedule() {
  return useQuery({
    queryKey: QK.doctorSchedule.all,
    queryFn: fetchDoctorSchedule,
    staleTime: 5 * 60_000,
  });
}

export function useSaveDoctorSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ section, values }) => saveDoctorSchedule(section, values),
    onSuccess: (_result, vars) => {
      qc.setQueryData(QK.doctorSchedule.all, (prev) => {
        if (!prev) return prev;
        return { ...prev, [vars.section]: vars.values };
      });
    },
  });
}
/* ──────────────────────────── Doctor · Calendar mutations ──────────────────────────── */

export function useUpdateAppointmentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apptId, status }) => updateAppointmentStatus(apptId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.doctorCalendar.all }),
  });
}

export function useRescheduleAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apptId, date, start }) => rescheduleAppointment(apptId, { date, start }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.doctorCalendar.all }),
  });
}

export function useReserveEmergencySlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slot) => reserveEmergencySlot(slot),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.doctorCalendar.all }),
  });
}
export function useUpdateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apptId, patch }) => updateAppointment(apptId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.doctorCalendar.all }),
  });
}
/* ──────────────────────────── Doctor · Consultation ──────────────────────────── */

export function useOpenConsultation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg) => {
      const patientId = typeof arg === 'string' ? arg : arg?.patientId;
      const patientName = typeof arg === 'string' ? undefined : arg?.patientName;
      const appointmentId = typeof arg === 'string' ? undefined : arg?.appointmentId;
      const extra = typeof arg === 'string' ? {} : {
        patientPhone: arg?.patientPhone,
        patientGender: arg?.patientGender,
        patientAge: arg?.patientAge,
      };
      return openConsultation(patientId, patientName, appointmentId, extra);
    },
    onSuccess: (record) => {
      qc.setQueryData(QK.consultation.record(record.recordId), record);
    },
  });
}

export function useOpenShortConsultation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg) => {
      const patientId = typeof arg === 'string' ? arg : arg?.patientId;
      const patientName = typeof arg === 'string' ? undefined : arg?.patientName;
      const extra = typeof arg === 'string' ? {} : {
        patientPhone: arg?.patientPhone,
        patientGender: arg?.patientGender,
        patientAge: arg?.patientAge,
      };
      return openShortConsultation(patientId, patientName, extra);
    },
    onSuccess: (record) => {
      qc.setQueryData(QK.consultation.record(record.recordId), record);
    },
  });
}

export function useConsultationRefData() {
  return useQuery({
    queryKey: ['consultation', 'ref-data'],
    queryFn: fetchConsultationRefData,
    staleTime: 10 * 60_000,
  });
}

export function useConsultation(recordId) {
  return useQuery({
    queryKey: QK.consultation.record(recordId),
    queryFn: () => fetchConsultation(recordId),
    enabled: !!recordId,
    staleTime: 30_000,
  });
}

/** Previous completed consultation for copy-from-last-visit. */
export function usePreviousConsultation(recordId) {
  return useQuery({
    queryKey: ['consultation-previous', recordId],
    queryFn: () => fetchPreviousConsultation(recordId),
    enabled: !!recordId,
    staleTime: 60_000,
  });
}

/** Per-patient historical drug-usage counts (byName / byCode). */
export function usePatientDrugUsage(patientId) {
  return useQuery({
    queryKey: ['patient-drug-usage', patientId],
    queryFn: () => fetchPatientDrugUsage(patientId),
    enabled: !!patientId,
    staleTime: 60_000,
  });
}

/** Last 10 distinct complaints for this patient. */
export function useRecentComplaints(patientId) {
  return useQuery({
    queryKey: ['patient-recent-complaints', patientId],
    queryFn: () => fetchPatientRecentComplaints(patientId),
    enabled: !!patientId,
    staleTime: 60_000,
  });
}

/** Doctor's own saved templates of a kind ('complaint' | 'pk' | 'medicine'). */
export function useConsultationTemplates(kind) {
  return useQuery({
    queryKey: ['consultation-templates', kind],
    queryFn: () => fetchConsultationTemplates(kind),
    enabled: !!kind,
    staleTime: 30_000,
  });
}

export function useCreateConsultationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createConsultationTemplate(payload),
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: ['consultation-templates', vars.kind] }),
  });
}

export function useDeleteConsultationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => deleteConsultationTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consultation-templates'] }),
  });
}

export function useSaveConsultationSection(recordId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, values }) => saveConsultationSection(recordId, sectionId, values),
    onSuccess: (result, vars) => {
      qc.setQueryData(QK.consultation.record(recordId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: {
            ...prev.sections,
            [vars.sectionId]: { values: vars.values, savedAt: result.savedAt },
          },
        };
      });
    },
  });
}

export function useCompleteConsultation(recordId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => completeConsultation(recordId),
    onSuccess: (result) => {
      qc.setQueryData(QK.consultation.record(recordId), (prev) =>
        prev ? { ...prev, status: 'completed', completedAt: result.completedAt } : prev
      );
    },
  });
}

export function useReopenConsultation(recordId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reopenConsultation(recordId),
    onSuccess: (result) => {
      qc.setQueryData(QK.consultation.record(recordId), (prev) =>
        prev ? { ...prev, status: 'draft', reopens: result.reopens } : prev
      );
    },
  });
}

export function useAddConsultationAddendum(recordId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note) => addConsultationAddendum(recordId, note),
    onSuccess: (record) => {
      qc.setQueryData(QK.consultation.record(recordId), (prev) =>
        prev ? { ...prev, addenda: record?.addenda || [] } : prev
      );
    },
  });
}

export function useSaveBodyMaps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items) => saveBodyMaps(items),
    onSuccess: (_r, items) => {
      qc.setQueryData(QK.masterForms.all, (prev) => (prev ? { ...prev, bodyMaps: items } : prev));
    },
  });
}
/* ──────────────────────────── Super Admin · Consultation records (D16) ──────────────────────────── */

export function useSAConsultations() {
  return useQuery({
    queryKey: ['sa', 'consultations'],
    queryFn: saListConsultations,
    staleTime: 30_000,
  });
}

export function useSAReopenConsultation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordId) => saReopenConsultation(recordId),
    onSuccess: (_r, recordId) => {
      qc.invalidateQueries({ queryKey: ['sa', 'consultations'] });
      qc.invalidateQueries({ queryKey: QK.consultation.record(recordId) });
    },
  });
}

/* ──────────────────────────── Doctor · Reports ──────────────────────────── */

export function useDoctorReports() {
  return useQuery({
    queryKey: ['doctor', 'reports'],
    queryFn: fetchDoctorReports,
    staleTime: 60_000,
  });
}

/* ──────────────────────────── Staff · Front desk (Phase 7) ──────────────────────────── */

export function useStaffToday() {
  return useQuery({
    queryKey: ['staff', 'today'],
    queryFn: fetchStaffToday,
    staleTime: 15_000,
  });
}

/* ──────────────────────────── Staff · Check-in actions (Batch 31) ──────────────────────────── */
/* Same mutation the doctor's calendar uses — one store, two scoped views —
   but invalidates BOTH surfaces so front desk and doctor stay in step. */

export function useStaffUpdateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apptId, status }) => updateAppointmentStatus(apptId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'today'] });
      qc.invalidateQueries({ queryKey: QK.doctorCalendar.all });
    },
  });
}

/* ──────────────────────────── Staff · Booking (Batch 32) ──────────────────────────── */

export function useStaffBookingContext() {
  return useQuery({
    queryKey: ['staff', 'booking-context'],
    queryFn: fetchStaffBookingContext,
    staleTime: 5 * 60_000,
  });
}

export function useStaffSlots(doctorId, date) {
  return useQuery({
    queryKey: ['staff', 'slots', doctorId, date],
    queryFn: () => fetchStaffSlots({ doctorId, date }),
    enabled: !!doctorId && !!date,
  });
}

// Patient booking wizard — same slots, patient-scoped route.
export function usePatientSlots(doctorId, date) {
  return useQuery({
    queryKey: ['patient', 'slots', doctorId, date],
    queryFn: () => fetchPatientSlots({ doctorId, date }),
    enabled: !!doctorId && !!date,
  });
}

export function useCreateStaffAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createStaffAppointment,
    onSuccess: (result) => {
      if (result?.created) {
        qc.invalidateQueries({ queryKey: ['staff'] });
        qc.invalidateQueries({ queryKey: QK.doctorCalendar.all });
      }
    },
  });
}

export function useConvertEmergencyHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: convertEmergencyHold,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: QK.doctorCalendar.all });
    },
  });
}

/* ──────────────────────────── Staff · Follow-ups + reschedule (Batch 33) ──────────────────────────── */

export function useStaffFollowups() {
  return useQuery({
    queryKey: ['staff', 'followups'],
    queryFn: fetchStaffFollowups,
    staleTime: 30_000,
  });
}

export function useStaffReschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apptId, date, start }) => rescheduleAppointment(apptId, { date, start }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: QK.doctorCalendar.all });
    },
  });
}

/* ──────────────────────────── Staff · Billing (Batch 35) ──────────────────────────── */

export function useBilling() {
  return useQuery({ queryKey: ['staff', 'billing'], queryFn: fetchBilling, staleTime: 15_000 });
}

function useBillingMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'billing'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'reports'] });  // pending balances are live there
      /* Invoices/payments/refunds move patient.balance, which is shown on
         the Super Admin patient list, the doctor's panel, the doctor's
         Accounts outstanding, and the patient's own home + bills. */
      qc.invalidateQueries({ queryKey: QK.patients.all });
      qc.invalidateQueries({ queryKey: ['doctor', 'patients'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] });
      qc.invalidateQueries({ queryKey: ['patient'] });
    },
  });
}
export function useCreateInvoice() { return useBillingMutation(createInvoice); }
export function useRecordPayment() { return useBillingMutation(recordPayment); }
export function useIssueRefund()   { return useBillingMutation(issueRefund); }

/* ── Coupons (B107) ── */
export function useCoupons() {
  return useQuery({ queryKey: ['staff', 'coupons'], queryFn: fetchCoupons, staleTime: 30_000 });
}

/** Preview a code against a subtotal; resolves { discount } or rejects with reason. */
export function useValidateCoupon() {
  return useMutation({ mutationFn: validateCoupon });
}

function useCouponMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'coupons'] });
      qc.invalidateQueries({ queryKey: ['staff', 'billing'] });   // times-used shows on day-end
    },
  });
}
export function useSaveCoupon()   { return useCouponMutation(saveCoupon); }
export function useToggleCoupon() { return useCouponMutation(toggleCoupon); }
export function useDeleteCoupon() { return useCouponMutation(deleteCoupon); }

export function useStaffDay(date) {
  return useQuery({
    queryKey: ['staff', 'day', date],
    queryFn: () => fetchStaffDay(date),
    enabled: !!date,
    staleTime: 15_000,
  });
}

/* ──────────────────────────── Staff · Merge patients (Batch 37) ──────────────────────────── */

export function useMyMergeRequests() {
  return useQuery({ queryKey: ['staff', 'merges'], queryFn: fetchMyMergeRequests, staleTime: 30_000 });
}

export function useSubmitMergeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitMergeRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'merges'] });
      qc.invalidateQueries({ queryKey: QK.dataCorrection.all }); // SA queue sees it live
    },
  });
}

/* ──────────────────────────── Staff · Complaints (Batch 38) ──────────────────────────── */

export function useComplaints() {
  return useQuery({ queryKey: ['staff', 'complaints'], queryFn: fetchComplaints, staleTime: 30_000 });
}

export function useComplaintMutation() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['staff', 'complaints'] });
  const create = useMutation({ mutationFn: createComplaint, onSuccess: invalidate });
  const update = useMutation({ mutationFn: updateComplaint, onSuccess: invalidate });
  return { create, update };
}

/* ──────────────────────────── Staff · Masseur console (Batch 39) ──────────────────────────── */

export function useMasseurDay(date) {
  return useQuery({
    queryKey: ['staff', 'masseur', date],
    queryFn: () => fetchMasseurDay(date),
    enabled: !!date,
    staleTime: 15_000,
  });
}

export function useUpdatePkSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePkSession,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: QK.doctorCalendar.all });
    },
  });
}

/* ──────────────────────────── Doctor · PK Consultant Sheet (Batch 40) ──────────────────────────── */

export function usePkTemplates() {
  return useQuery({ queryKey: ['doctor', 'pk-templates'], queryFn: fetchPkTemplates, staleTime: 10 * 60_000 });
}

export function useCreatePkCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPkCourse,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] }),
  });
}

export function usePkCourses(doctorFilter) {
  return useQuery({
    queryKey: ['doctor', 'pk-courses', doctorFilter],
    queryFn: () => fetchPkCourses({ doctorFilter }),
    staleTime: 30_000,
  });
}

export function usePkConsent(courseId, enabled = true) {
  return useQuery({
    queryKey: ['pk-consent', courseId],
    queryFn: () => fetchPkConsent(courseId),
    enabled: !!courseId && enabled,
  });
}

export function useSendPkConsentOtp() {
  return useMutation({ mutationFn: sendPkConsentOtp });
}

export function useVerifyPkConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: verifyPkConsent,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] });
      qc.invalidateQueries({ queryKey: ['pk-consent', vars?.courseId] });
    },
  });
}

export function usePkCourse(courseId) {
  return useQuery({
    queryKey: ['doctor', 'pk-course', courseId],
    queryFn: () => fetchPkCourse(courseId),
    enabled: !!courseId,
  });
}

export function useUpdatePkCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePkCourse,
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-course', vars.courseId] });
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] });
    },
  });
}

/* ──────────────────────────── Doctor · Accounts & Inventory (Batch 41) ──────────────────────────── */

export function useDoctorAccounts(period) {
  return useQuery({
    queryKey: ['doctor', 'accounts', period],
    queryFn: () => fetchDoctorAccounts({ period }),
    staleTime: 20_000,
  });
}

export function useAdjustInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adjustInventory,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] }),
  });
}

/* ──────────────────────────── Doctor · Settings (Batch 42) ──────────────────────────── */

export function useDoctorSettings() {
  return useQuery({ queryKey: ['doctor', 'settings'], queryFn: fetchDoctorSettings, staleTime: 60_000 });
}

/* ── Doctor onboarding documents (authed self-service) ── */
export function useDoctorOnboardingStatus({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['doctor', 'onboarding-status'],
    queryFn: fetchDoctorOnboardingStatus,
    staleTime: 60_000,
    enabled,   // a non-doctor clinic admin has no doctor profile → skip the 403
  });
}
export function useSubmitDoctorKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitDoctorKyc,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor', 'onboarding-status'] });
      // Final-doc completion lifts docs_locked on the user — refresh /me so the
      // shell drops locked-mode.
      qc.invalidateQueries({ queryKey: QK.auth.me });
    },
  });
}

/* ── Clinic admin onboarding documents (authed self-service) ── */
export function useClinicOnboardingStatus() {
  return useQuery({
    queryKey: ['clinic', 'onboarding-status'],
    queryFn: fetchClinicOnboardingStatus,
    staleTime: 60_000,
  });
}
export function useSubmitClinicKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitClinicKyc,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic', 'onboarding-status'] });
      qc.invalidateQueries({ queryKey: QK.auth.me });
    },
  });
}

export function useSaveDoctorSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveDoctorSettings,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor', 'settings'] }),
  });
}

/* ──────────────────────────── Staff · Pharmacy queue (Batch 43) ──────────────────────────── */

export function usePharmacyQueue() {
  return useQuery({ queryKey: ['staff', 'pharmacy'], queryFn: fetchPharmacyQueue, staleTime: 15_000 });
}

export function useDispenseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dispenseOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'pharmacy'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] }); // inventory moved
    },
  });
}

/* ──────────────────────────── Staff · PK consumables desk (Assistant, T3) ──────────────────────────── */

export function usePkConsumables() {
  return useQuery({ queryKey: ['staff', 'pk-consumables'], queryFn: fetchPkConsumables, staleTime: 15_000 });
}

export function useAddPkConsumable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: addPkConsumable,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'pk-consumables'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] }); // inventory totals
    },
  });
}

export function useAdjustPkConsumable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adjustPkConsumable,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', 'pk-consumables'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] }); // inventory moved
    },
  });
}

/* ──────────────────────────── Staff · RMO pre-PK clearance (Batch 44) ──────────────────────────── */

export function useRmoQueue() {
  return useQuery({ queryKey: ['staff', 'rmo'], queryFn: fetchRmoQueue, staleTime: 15_000 });
}

export function useSubmitRmoClearance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitRmoClearance,
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['staff', 'rmo'] });
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-course', vars.courseId] });
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] });
    },
  });
}

/* ──────────────────────────── Doctor · Accounts complete (Batch 45) ──────────────────────────── */

export function useAccountsExtras() {
  return useQuery({ queryKey: ['doctor', 'accounts-extras'], queryFn: fetchAccountsExtras, staleTime: 20_000 });
}
export function useCosting() {
  return useQuery({ queryKey: ['doctor', 'costing'], queryFn: fetchCosting, staleTime: 60_000 });
}
function useAcctMutation(fn, alsoInventory) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor', 'accounts-extras'] });
      if (alsoInventory) qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] });
    },
  });
}
export function useAddExpenditure()      { return useAcctMutation(addExpenditure); }
export function useMarkExpenditurePaid() { return useAcctMutation(markExpenditurePaid); }
export function useCreatePurchaseOrder() { return useAcctMutation(createPurchaseOrder); }
export function useReceivePurchaseOrder(){ return useAcctMutation(receivePurchaseOrder, true); }
export function useToggleCompliance() {
  const qc = useQueryClient();
  const key = ['doctor', 'accounts-extras'];
  // TC_ACC_COMP_002: tick optimistically so the checkbox flips instantly instead
  // of waiting for the server round-trip + refetch (the "noticeable delay").
  return useMutation({
    mutationFn: toggleCompliance,
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      qc.setQueryData(key, (old) => (old?.compliance
        ? { ...old, compliance: old.compliance.map((t) => (t.id === id ? { ...t, done: !t.done } : t)) }
        : old));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
export function useSaveCosting() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: saveCosting, onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor', 'costing'] }) });
}

/* ──────────────────────────── Doctor · Settings clinic view (Batch 45) ──────────────────────────── */

export function useMyClinicConfig() {
  return useQuery({ queryKey: ['doctor', 'clinic-config'], queryFn: fetchMyClinicConfig, staleTime: 5 * 60_000 });
}

/* ──────────────────────────── Staff · Incident log (Batch 46) ──────────────────────────── */

export function useIncidents(win) {
  return useQuery({ queryKey: ['staff', 'incidents', win], queryFn: () => fetchIncidents({ window: win }), staleTime: 30_000 });
}
export function useIncidentMutation() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['staff', 'incidents'] });
  const create = useMutation({ mutationFn: createIncident, onSuccess: invalidate });
  const resolve = useMutation({ mutationFn: resolveIncident, onSuccess: invalidate });
  return { create, resolve };
}

/* ──────────────────────────── Staff · RMO monitor + Masseur history (Batch 47) ──────────────────────────── */

export function useRmoMonitor() {
  return useQuery({ queryKey: ['staff', 'rmo', 'monitor'], queryFn: fetchRmoMonitor, staleTime: 10_000, refetchInterval: 30_000 });
}
export function useMasseurHistory(days) {
  return useQuery({ queryKey: ['staff', 'masseur', 'history', days], queryFn: () => fetchMasseurHistory({ days }), staleTime: 30_000 });
}

/* ──────────────────────────── Patient portal (Batch 48) ──────────────────────────── */

export function usePatientHome() {
  return useQuery({ queryKey: ['patient', 'home'], queryFn: fetchPatientHome, staleTime: 20_000 });
}
export function usePatientAppointments() {
  return useQuery({ queryKey: ['patient', 'appointments'], queryFn: fetchPatientAppointments, staleTime: 15_000 });
}
export function usePatientBookingContext() {
  return useQuery({ queryKey: ['patient', 'booking-context'], queryFn: fetchPatientBookingContext, staleTime: 5 * 60_000 });
}
export function usePatientBookingMutation() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['patient'] });
    qc.invalidateQueries({ queryKey: ['staff'] });          // front desk sees it
    qc.invalidateQueries({ queryKey: QK.doctorCalendar.all }); // doctor's diary too
  };
  const create = useMutation({ mutationFn: createPatientAppointment, onSuccess: done });
  const cancel = useMutation({ mutationFn: cancelPatientAppointment, onSuccess: done });
  return { create, cancel };
}

/* ──────────────────────────── Patient · Records + PK journey (Batch 49) ──────────────────────────── */

export function usePatientRecords() {
  return useQuery({ queryKey: ['patient', 'records'], queryFn: fetchPatientRecords, staleTime: 60_000 });
}
export function usePatientRecord(recordId) {
  return useQuery({ queryKey: ['patient', 'record', recordId], queryFn: () => fetchPatientRecord(recordId), enabled: !!recordId });
}
export function usePatientPkJourney() {
  return useQuery({ queryKey: ['patient', 'pk-journey'], queryFn: fetchPatientPkJourney, staleTime: 30_000 });
}
export function usePatientLabReports() {
  return useQuery({ queryKey: ['patient', 'lab-reports'], queryFn: fetchPatientLabReports, staleTime: 60_000 });
}
export function usePatientImages() {
  return useQuery({ queryKey: ['patient', 'images'], queryFn: fetchPatientImages, staleTime: 60_000 });
}
export function usePatientUploads() {
  return useQuery({ queryKey: ['patient', 'uploads'], queryFn: fetchPatientUploads, staleTime: 30_000 });
}
export function useUploadPatientFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadPatientFile,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', 'uploads'] }),
  });
}
export function useDeletePatientUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deletePatientUpload,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', 'uploads'] }),
  });
}

/* ── Clinic-side authoring: patient lab reports + uploads (B141) ── */
export function useClinicPatientLabs(patientId) {
  return useQuery({
    queryKey: ['clinic', 'patient-labs', patientId],
    queryFn: () => fetchClinicPatientLabs(patientId),
    enabled: !!patientId,
    staleTime: 30_000,
  });
}
export function useClinicDaySummary({ clinicId, date } = {}) {
  return useQuery({
    queryKey: ['clinic', 'day-summary', clinicId, date || 'today'],
    queryFn: () => fetchClinicDaySummary({ clinicId, date }),
    enabled: !!clinicId,
    staleTime: 30_000,
  });
}
export function useCreateClinicPatientLab(patientId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClinicPatientLab,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic', 'patient-labs', patientId] });
      qc.invalidateQueries({ queryKey: ['patient', 'lab-reports'] });
    },
  });
}
export function useDeleteClinicPatientLab(patientId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteClinicPatientLab,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic', 'patient-labs', patientId] });
      qc.invalidateQueries({ queryKey: ['patient', 'lab-reports'] });
    },
  });
}
export function useClinicPatientUploads(patientId) {
  return useQuery({
    queryKey: ['clinic', 'patient-uploads', patientId],
    queryFn: () => fetchClinicPatientUploads(patientId),
    enabled: !!patientId,
    staleTime: 30_000,
  });
}
export function useUploadClinicPatientFile(patientId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadClinicPatientFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic', 'patient-uploads', patientId] });
      qc.invalidateQueries({ queryKey: ['patient', 'uploads'] });
    },
  });
}
export function useDeleteClinicPatientUpload(patientId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteClinicPatientUpload,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic', 'patient-uploads', patientId] });
      qc.invalidateQueries({ queryKey: ['patient', 'uploads'] });
    },
  });
}

/* ──────────────────────────── Patient · Bills + profile (Batch 50) ──────────────────────────── */

export function usePatientBills() {
  return useQuery({ queryKey: ['patient', 'bills'], queryFn: fetchPatientBills, staleTime: 20_000 });
}
export function usePatientBill(invoiceId) {
  return useQuery({ queryKey: ['patient', 'bill', invoiceId], queryFn: () => fetchPatientBill(invoiceId), enabled: !!invoiceId });
}
export function useInitiatePatientPayment() {
  return useMutation({ mutationFn: initiatePatientPayment });
}

export { verifyPatientPayment };
export function usePatientProfile() {
  return useQuery({ queryKey: ['patient', 'profile'], queryFn: fetchPatientProfile, staleTime: 60_000 });
}
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationPrefs,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', 'profile'] }),
  });
}
export function useRequestProfileUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: requestProfileUpdate,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.dataCorrection.all }),
  });
}

/* ──────────────────────────── Patient · Consent · Complaint · Feedback (Batch 51) ──────────────────────────── */

export function usePatientConsent() {
  return useQuery({ queryKey: ['patient', 'consent'], queryFn: fetchPatientConsent, staleTime: 30_000 });
}
export function useSignPatientConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: signPatientConsent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient'] });
      qc.invalidateQueries({ queryKey: ['staff', 'masseur'] }); // consent gate opens
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] });
    },
  });
}
export function useMyComplaints() {
  return useQuery({ queryKey: ['patient', 'complaints'], queryFn: fetchMyComplaints, staleTime: 30_000 });
}
export function useFilePatientComplaint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: filePatientComplaint,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'complaints'] });
      qc.invalidateQueries({ queryKey: ['staff', 'complaints'] }); // lands in the register
    },
  });
}
export function useFeedbackTargets() {
  return useQuery({ queryKey: ['patient', 'feedback'], queryFn: fetchFeedbackTargets, staleTime: 30_000 });
}
export function useSubmitPatientFeedback() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: submitPatientFeedback, onSuccess: () => qc.invalidateQueries({ queryKey: ['patient', 'feedback'] }) });
}

/* ──────────────────────────── Staff · Quick add patient (Batch 52) ──────────────────────────── */

export function useQuickAddPatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: quickAddPatient,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.patients.all }); // Super Admin list + derived summary
      qc.invalidateQueries({ queryKey: ['staff'] });       // booking search, front desk
      qc.invalidateQueries({ queryKey: ['doctor'] });      // doctor's patient panel + summary
    },
  });
}

export function useMasseurCalendar(date) {
  return useQuery({ queryKey: ['staff', 'masseur', 'calendar', date], queryFn: () => fetchMasseurCalendar(date), enabled: !!date, staleTime: 15_000 });
}

/* ──────────────────────────── Clinic Panel (Batch 54) ──────────────────────────── */

export function useClinicPanel(clinicId) {
  return useQuery({
    queryKey: ['clinic-panel', clinicId || 'mine'],
    queryFn: () => fetchClinicPanel(clinicId),
    staleTime: 20_000,
    enabled: !!clinicId,
  });
}
export function useMyClinics() {
  return useQuery({ queryKey: ['my-clinics'], queryFn: fetchMyClinics, staleTime: 5 * 60_000 });
}
/* Server-side clinic patient search — the whole list, not just the panel's first page. */
export function useClinicPatientsSearch(clinicId, search) {
  const term = (search || '').trim();
  return useQuery({
    queryKey: ['clinic-patients-search', clinicId || 'mine', term],
    queryFn: () => fetchClinicPatientsSearch(clinicId, { search: term, limit: 50 }),
    enabled: !!clinicId && term.length > 0,
    staleTime: 15_000,
    keepPreviousData: true,
  });
}
export function useCreateClinicStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClinicStaff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic-panel'] });
      qc.invalidateQueries({ queryKey: QK.staff.all });   // Super Admin's staff list + summary
    },
  });
}

/* ──────────────────────────── D21 · Per-clinic GST settings (Batch 56) ──────────────────────────── */

export function useClinicBillingSettings(clinicId) {
  return useQuery({
    queryKey: ['clinic-billing-settings', clinicId || 'mine'],
    queryFn: () => fetchClinicBillingSettings(clinicId),
    staleTime: 60_000,
  });
}
export function useSaveClinicBillingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveClinicBillingSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic-billing-settings'] });
      qc.invalidateQueries({ queryKey: ['staff', 'billing'] });   // new invoices pick up the rate
    },
  });
}

/* ──────────────────────────── D22 · Clinic-detail editing (Batch 57) ──────────────────────────── */

export function useUpdateClinicDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateClinicDetails,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic-panel'] });
      qc.invalidateQueries({ queryKey: QK.clinics.all });  // SA list reflects the change
    },
  });
}

export function useAdminPatientDetail(id) {
  return useQuery({
    queryKey: ['adminPatientDetail', id],
    queryFn: () => fetchAdminPatientDetail(id),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/* ──────────────────────────── Communication Hub (B58) ──────────────────────────── */

export function useCommOverview() {
  return useQuery({
    queryKey: QK.comm.overview,
    queryFn: fetchCommOverview,
    staleTime: 30_000,
  });
}

export function useCommMessages(filters = {}) {
  return useQuery({
    queryKey: QK.comm.messages(filters),
    queryFn: () => fetchCommMessages(filters),
    staleTime: 15_000,
    keepPreviousData: true,
  });
}

export function useCommThread(id) {
  return useQuery({
    queryKey: QK.comm.thread(id),
    queryFn: () => fetchCommThread(id),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useSendCommMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendCommMessage,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.comm.all });
    },
  });
}

export function useReplyCommMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: replyCommMessage,
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: QK.comm.thread(vars.id) });
      qc.invalidateQueries({ queryKey: ['comm', 'messages'] });
      qc.invalidateQueries({ queryKey: QK.comm.overview });
    },
  });
}

export function useMarkCommRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markCommRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comm', 'messages'] });
      qc.invalidateQueries({ queryKey: QK.comm.overview });
    },
  });
}

export function useCommTemplates() {
  return useQuery({
    queryKey: QK.comm.templates,
    queryFn: fetchCommTemplates,
    staleTime: 5 * 60_000,
  });
}

export function useCommDirectory() {
  return useQuery({
    queryKey: QK.comm.directory,
    queryFn: fetchCommDirectory,
    staleTime: 5 * 60_000,
  });
}

export function useCommCampaigns() {
  return useQuery({
    queryKey: QK.comm.campaigns,
    queryFn: fetchCommCampaigns,
    staleTime: 60_000,
  });
}

export function useCreateCommCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCommCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.comm.campaigns }),
  });
}

export function useCommCertificates() {
  return useQuery({
    queryKey: QK.comm.certs,
    queryFn: fetchCommCertificates,
    staleTime: 60_000,
  });
}

export function useIssueCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: issueCommCertificate,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.comm.certs }),
  });
}

/* ──────────────────────────── Public booking (B59) ──────────────────────────── */

export function usePublicClinics(coords) {
  return useQuery({
    queryKey: ['public', 'clinics', coords || null],
    queryFn: () => fetchPublicClinics(coords || {}),
    staleTime: 5 * 60_000,
  });
}

export function usePublicSlots(doctorId, date) {
  return useQuery({
    queryKey: ['public', 'slots', doctorId, date],
    queryFn: () => fetchPublicSlots({ doctorId, date }),
    enabled: !!doctorId && !!date,
    staleTime: 30_000,
  });
}

export function usePublicBook() {
  return useMutation({ mutationFn: publicBook });
}

// imperative returning-patient lookup (used on demand, not as a live query)
export async function lookupReturningPatient(phone) {
  return fetchReturningPatient(phone);
}

/* ──────────────────────────── Onboarding invites + KYC ──────────────────────────── */

/* Public (no-auth) — the tokenised document-submission link. */
export function usePublicOnboard(token) {
  return useQuery({
    queryKey: ['public', 'onboard', token],
    queryFn: () => fetchPublicOnboard(token),
    enabled: !!token,
    staleTime: 0,
    retry: false,
  });
}
export function useSendOnboardOtp(token) {
  return useMutation({ mutationFn: () => sendOnboardOtp(token) });
}
export function useVerifyOnboardOtp(token) {
  return useMutation({ mutationFn: (body) => verifyOnboardOtp(token, body) });
}
export function useSubmitOnboardDoc(token) {
  return useMutation({ mutationFn: (body) => submitOnboardDoc(token, body) });
}
export function usePublicOnboardSchema(token, enabled) {
  return useQuery({
    queryKey: ['public', 'onboard', 'schema', token],
    queryFn: () => fetchOnboardSchema(token),
    enabled: !!token && !!enabled,
    staleTime: 5 * 60_000,
  });
}
export function useSaveOnboardSection(token) {
  return useMutation({ mutationFn: (body) => saveOnboardSection(token, body) });
}

/* Authed — SA / Clinic invite management + KYC review. */
export function useInvites(kind) {
  return useQuery({
    queryKey: ['invites', kind || 'all'],
    queryFn: () => fetchInvites(kind),
    staleTime: 30_000,
  });
}
function useInviteMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] });
      qc.invalidateQueries({ queryKey: ['kyc'] });
    },
  });
}
export function useCreateInvite() { return useInviteMutation(createInvite); }
export function useResendInvite() { return useInviteMutation(resendInvite); }
export function useRevokeInvite() { return useInviteMutation(revokeInvite); }

export function useKycSubjects() {
  return useQuery({
    queryKey: ['kyc', 'subjects'],
    queryFn: fetchKycSubjects,
    staleTime: 30_000,
  });
}
export function useKycSubject(ownerType, ownerId) {
  return useQuery({
    queryKey: ['kyc', 'subject', ownerType, ownerId],
    queryFn: () => fetchKycSubject({ ownerType, ownerId }),
    enabled: !!ownerType && !!ownerId,
    staleTime: 10_000,
  });
}
function useKycMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kyc'] }),
  });
}
export function useVerifyKycDoc() { return useKycMutation(verifyKycDoc); }
export function useRejectKycDoc() { return useKycMutation(rejectKycDoc); }
export function useApproveKycSubject() { return useKycMutation(approveKycSubject); }

/* Public self-registration (no invite) — clinic search + OTP + submit. */
export function useRegisterClinicSearch(q, enabled = true) {
  return useQuery({
    queryKey: ['public', 'register', 'clinics', q || ''],
    queryFn: () => fetchRegisterClinics(q),
    enabled,
    staleTime: 60_000,
  });
}
export function useSendRegisterOtp() {
  return useMutation({ mutationFn: (body) => sendRegisterOtp(body) });
}
export function useRegisterDoctor() {
  return useMutation({ mutationFn: (body) => registerDoctor(body) });
}
export function useRegisterClinic() {
  return useMutation({ mutationFn: (body) => registerClinic(body) });
}

/* ──────────────────────────── PK day-log (B62) ──────────────────────────── */
export function useLogPkDay(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => logPkDay(courseId, payload),
    onSuccess: (res) => {
      if (res?.course) qc.setQueryData(['doctor', 'pk-course', courseId], res);
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-course', courseId] });
      qc.invalidateQueries({ queryKey: ['doctor', 'pk-courses'] });
    },
  });
}

/* ──────────────────────────── Public payments (B65) ──────────────────────────── */
export async function createPaymentOrder(payload) { return _createPaymentOrder(payload); }
export async function verifyPayment(payload) { return _verifyPayment(payload); }


/* ────────────────── Online invoice payment · Razorpay (authenticated) ──────────────────
   A payment made online against an existing invoice moves the patient balance,
   which is shown on the Super Admin patient list, the doctor's panel + Accounts,
   the doctor's reports, and the patient's own home + bills — so after a verified
   payment we invalidate the same surfaces the desk-payment mutation does. */

function invalidateBillingEverywhere(qc) {
  qc.invalidateQueries({ queryKey: ['staff', 'billing'] });
  qc.invalidateQueries({ queryKey: ['doctor', 'reports'] });
  qc.invalidateQueries({ queryKey: QK.patients.all });
  qc.invalidateQueries({ queryKey: ['doctor', 'patients'] });
  qc.invalidateQueries({ queryKey: ['doctor', 'accounts'] });
  qc.invalidateQueries({ queryKey: ['patient'] });
}

/* Shared Razorpay dance: order (server-priced) → Checkout → verify (server-signed).
   Returns { payNow, isPaying, error, reset }. payNow(invoice) resolves with the
   verify result ({ status, ok, message, ... }) or a { ok:false, code } for the
   gateway-off / dismissed / error branches, so the caller can render outcomes. */
function useOnlineInvoicePayment({ order, verify, onSettled }) {
  const qc = useQueryClient();
  const [isPaying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const payNow = useCallback((invoice, prefill = {}) => {
    return new Promise((resolve) => {
      setError('');
      setPaying(true);
      const finish = (result) => { setPaying(false); resolve(result); };

      (async () => {
        try {
          const ord = await order(invoice.invoiceId);

          if (ord.status === 503) {
            setError('Online payment isn\u2019t available yet \u2014 please pay at the clinic.');
            return finish({ ok: false, code: 'gateway_off', message: ord.message });
          }
          if (ord.status === 422) {
            setError(ord.message || 'This invoice is already fully paid.');
            return finish({ ok: false, code: 'settled', message: ord.message });
          }
          if (!ord.orderId || !ord.keyId) {
            setError('Could not start payment. Please try again or pay at the clinic.');
            return finish({ ok: false, code: 'error' });
          }

          const ready = await loadRazorpay();
          if (!ready || !window.Razorpay) {
            setError('Payment window failed to load. Check your connection and try again.');
            return finish({ ok: false, code: 'error' });
          }

          const rzp = new window.Razorpay({
            key: ord.keyId,
            order_id: ord.orderId,
            amount: ord.amount,
            currency: ord.currency || 'INR',
            name: ord.name || '1Ayurved',
            description: `Invoice ${invoice.invoiceId}`,
            prefill: ord.prefill || prefill,
            theme: { color: '#2f6b46' },
            handler: async (r) => {
              const res = await verify(invoice.invoiceId, {
                razorpay_order_id: r.razorpay_order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              if (res.status === 409) {
                setError('Payment received, but this invoice was already settled. Our team will process a refund.');
              } else if (res.status >= 400) {
                setError(res.message || 'Payment verification failed.');
              }
              if (onSettled) onSettled(qc);
              finish(res);
            },
            modal: { ondismiss: () => finish({ ok: false, code: 'dismissed' }) },
          });
          rzp.open();
        } catch (e) {
          setError('Something went wrong starting payment. Please try again.');
          finish({ ok: false, code: 'error' });
        }
      })();
    });
  }, [order, verify, onSettled, qc]);

  return { payNow, isPaying, error, reset: () => setError('') };
}

export function useStaffInvoiceOnlinePay() {
  return useOnlineInvoicePayment({
    order: staffInvoiceOnlineOrder,
    verify: staffInvoiceOnlineVerify,
    onSettled: invalidateBillingEverywhere,
  });
}

export function usePatientBillOnlinePay() {
  return useOnlineInvoicePayment({
    order: patientBillOnlineOrder,
    verify: patientBillOnlineVerify,
    onSettled: (qc) => qc.invalidateQueries({ queryKey: ['patient'] }),
  });
}


/* ── Master Forms · version history + rollback ── */
export function useMasterFormVersions(enabled = true) {
  return useQuery({
    queryKey: ['master-forms', 'versions'],
    queryFn: fetchMasterFormVersions,
    enabled,
    staleTime: 0,
  });
}

export function useRestoreMasterForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => restoreMasterFormVersion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.masterForms.all });
      qc.invalidateQueries({ queryKey: ['master-forms', 'versions'] });
    },
  });
}

/* ── Drugs bulk import ── */
export function useImportDrugs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows) => importDrugs(rows),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drugs'] });
    },
  });
}

/* ── Patient by id + communication consent ── */
export function usePatient(id) {
  return useQuery({ queryKey: ['patient', 'byId', id], queryFn: () => fetchPatient(id), enabled: !!id });
}

export function useSetCommConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, flags }) => setCommConsent(id, flags),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'byId'] });
      qc.invalidateQueries({ queryKey: QK.patients.all });
    },
  });
}
